import _ from 'lodash';
import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { DEFAULT_CARDANO_CHAIN_ID } from '@/constants/DEFAULT_CARDANO_ID';
import { fromWei, getScanLink } from '@/sdk/utils';
import { calculateGasForReversBridge } from '@/helpers/milkomeda-wrapped-smartcontract/milkomeda-wsc-calculation';
import { PortfolioFarm } from '@/sdk/entities/portfolioFarm';
import {
  INotification,
  NotificationStatus,
} from '@/store/modules/notifications/models/notification.interface';
import { IFarmingHarvestForm } from '../models/farming-harvest-form.interface';
import { Token } from '@/sdk/entities/token';
import { BridgeToken } from '@/composables/milkomeda-wrapped-smartcontract/models/bridge-token';
import { useNotifications } from '@/store/modules/notifications/useNotifications';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { useFarmingMilkomedaWSCBridge } from '@/store/modules/farming/useFarmingMilkomedaWSCBridge';
import { useFarmingMilkomedaWSCUnwrapBridge } from '@/store/modules/farming/useFarmingMilkomedaWSCUnwrapBridge';
import { useFarmingTransactions } from '@/composables/farming/useFarmingTransactions';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';

export const useFarmingHarvest = defineStore('farmingHarvest', () => {
  const { t } = useI18n();
  const { getTokenBySymbolAndChainId, getTokenByAddressAndChainId, isPresentTokenIntoNetwork } =
    useTokens();
  const {
    setBridgeTokensFromCardano,
    setIsEVMFromCardano,
    setHasBridgeFromMilkomeda,
    $reset: resetMilkomedaWSCBridge,
  } = useFarmingMilkomedaWSCBridge();
  const {
    setBridgeTokensFromMilkomeda,
    setIsEVMFromMilkomeda,
    $reset: resetMilkomedaWSCUnwrapBridge,
  } = useFarmingMilkomedaWSCUnwrapBridge();
  const { harvest, harvestWithUnwrap } = useFarmingTransactions();
  const { addNotification } = useNotifications();

  const farmingHarvestForm = reactive<IFarmingHarvestForm>({
    farm: null,
  });

  const cardanoADATokenForBridge = computed<Token>(() => {
    return getTokenBySymbolAndChainId('ADA', +DEFAULT_CARDANO_CHAIN_ID);
  });
  const harvestToken = computed(() => {
    if (!DEFAULT_NETWORK_ID || !farmingHarvestForm.farm) return;

    return getTokenByAddressAndChainId(farmingHarvestForm.farm.tokenToMint, +DEFAULT_NETWORK_ID);
  });
  const isNeedHarvestWithUnwrap = computed(() => {
    return (
      // When Cardano then ADA is WADA
      (ENABLE_FAKE_CARDANO_NETWORK && harvestToken.value?.isETHToken()) ||
      (!ENABLE_FAKE_CARDANO_NETWORK && harvestToken.value?.isWETHToken())
    );
  });

  function $reset(): void {
    farmingHarvestForm.farm = null;
  }

  function checkHarvestForBridgeFromMilkomeda(amountInWei: string, token: Token) {
    if (!ENABLE_FAKE_CARDANO_NETWORK) return;
    resetMilkomedaWSCBridge();
    resetMilkomedaWSCUnwrapBridge();

    const bridgeTokens: BridgeToken[] = [];
    if (isPresentTokenIntoNetwork(token.symbol!, +DEFAULT_CARDANO_CHAIN_ID)) {
      bridgeTokens.push({
        amount: fromWei(amountInWei, token.decimals).toString(),
        token: token,
      });
    }

    setIsEVMFromCardano(false);
    setHasBridgeFromMilkomeda(!!bridgeTokens.length);

    if (bridgeTokens.length) {
      setIsEVMFromMilkomeda(false);
      setBridgeTokensFromMilkomeda(bridgeTokens);
    } else {
      resetMilkomedaWSCUnwrapBridge();
    }

    const gas = calculateGasForReversBridge(cardanoADATokenForBridge.value, bridgeTokens);
    setBridgeTokensFromCardano(
      gas ? [gas] : [{ amount: '0', token: cardanoADATokenForBridge.value }],
    );
  }

  function setHarvestFromFarm(farm: PortfolioFarm): void {
    farmingHarvestForm.farm = farm;
  }

  async function doHarvest() {
    console.groupCollapsed('[FARMING:HARVEST] doHarvest');
    const cfarmingHarvestForm: IFarmingHarvestForm = _.cloneDeep(farmingHarvestForm);

    if (!cfarmingHarvestForm.farm) return;

    const portfolioOfFarm = cfarmingHarvestForm.farm.portfolio;
    const notificationId = `farming_harvest_${portfolioOfFarm.contractAddress}`;

    try {
      addNotification(
        getHarvestNotificationOptions(portfolioOfFarm.name, {
          id: notificationId,
          status: 'inProgress',
        }),
      );

      const harvestFn = isNeedHarvestWithUnwrap.value ? harvestWithUnwrap : harvest;
      const transactionReceipt = await harvestFn(farmingHarvestForm.farm as PortfolioFarm);

      const explorerChainId = ENABLE_FAKE_CARDANO_NETWORK ? +DEFAULT_NETWORK_ID! : undefined;
      addNotification(
        getHarvestNotificationOptions(portfolioOfFarm.name, {
          id: notificationId,
          status: 'success',
          explorerLink: getScanLink(
            transactionReceipt.transactionHash,
            'transaction',
            explorerChainId,
          ),
        }),
      );
    } catch (error) {
      console.error(`[FARMING:HARVEST] Happen error during harvest operation. ERROR : `, error);
      if (error.name === 'ProviderRpcError') {
        console.error(`[ERROR] ProviderRpcError. Error details : `, {
          code: error.code,
          data: error.data,
        });
      }

      addNotification(
        getHarvestNotificationOptions(portfolioOfFarm.name, {
          id: notificationId,
          status: 'error',
        }),
      );

      throw error;
    } finally {
      console.groupEnd();
    }
  }

  // NOTIFICATIONS

  function getHarvestNotificationOptions(
    portfolioName: string,
    options: {
      status: NotificationStatus;
      id: string;
      explorerLink?: string;
    },
  ): INotification {
    const content = buildHarvestNotificationContent(portfolioName, options.status);

    return {
      ...options,
      content,
    };
  }

  function buildHarvestNotificationContent(portfolioName: string, status: NotificationStatus) {
    const content = `${t('harvestingEarnings')} ${t('from')} ${portfolioName} ${t('farm')}`;

    const notifications: Record<NotificationStatus, string> = {
      inProgress: content,
      success: `${content} ${t('succeeded')}`,
      error: `${content} ${t('failed')}`,
    };

    return notifications[status];
  }
  // ====

  return {
    farmingHarvestForm,
    checkHarvestForBridgeFromMilkomeda,
    setHarvestFromFarm,
    doHarvest,
    $reset,
  };
});
